import { setCustomUrl, trackPageView } from '@snowplow/browser-tracker';
import produce from 'immer';
import { debounce, isNumber } from 'lodash';
import ReactGA from 'react-ga';
import type { Optional, Primitive } from 'utility-types';

import { GADimension } from '@swf/core/enums';
import type { TrackingContextValue } from '@swf/core/providers/TrackingContext';
import type { ButtonClick } from '@swf/generated/analytics/buttonClick';
import { buttonClick } from '@swf/generated/analytics/buttonClick';
import type { Entity } from '@swf/generated/analytics/entities';
import type { ErrorView } from '@swf/generated/analytics/errorView';
import { errorView } from '@swf/generated/analytics/errorView';
import type { InputFill } from '@swf/generated/analytics/inputFill';
import { inputFill } from '@swf/generated/analytics/inputFill';
import type { InputFocus } from '@swf/generated/analytics/inputFocus';
import { inputFocus } from '@swf/generated/analytics/inputFocus';
import type { LinkClick } from '@swf/generated/analytics/linkClick';
import { linkClick } from '@swf/generated/analytics/linkClick';
import type { ResultsView } from '@swf/generated/analytics/resultsView';
import { resultsView } from '@swf/generated/analytics/resultsView';
import type { TabClick } from '@swf/generated/analytics/tabClick';
import { tabClick } from '@swf/generated/analytics/tabClick';

import { GAEvent } from './utils';

type InputData = Omit<
  Optional<InputFocus & InputFill, 'fieldName' | 'isAutoFilled'>,
  'fieldType'
> & {
  fieldType: InputFill['fieldType'] | 'hidden';
};

export const buttonPressed = (
  context: TrackingContextValue,
  { value, ...data }: Optional<ButtonClick, 'buttonName'> & { value?: number },
  trackingDimensions?: Record<string, string[] | Primitive> | undefined
) => {
  if (!data.buttonName) return;
  GAEvent({ action: 'Button Pressed', label: data.buttonName, value }, context, trackingDimensions);
  buttonClick(data as ButtonClick, context);
};

export const inputFocused = (context: TrackingContextValue, data: InputData) => {
  if (!data.fieldName) return;
  GAEvent({ action: 'Input Focused', label: data.fieldName }, context);
  inputFocus(data as InputFocus, context);
};

export const inputFilled = (
  context: TrackingContextValue,
  data: InputData,
  stringValue?: string
) => {
  if (!data.fieldName) return;
  if (typeof data.strInput === 'string' && !data.strInput.trim()) return;
  if (!['boolean', 'undefined'].includes(typeof data.boolInput)) return;
  const value = isNumber(data.numInput) ? data.numInput : undefined;

  GAEvent({ action: 'Input Filled', label: data.fieldName, value }, context, {
    [GADimension.InputValue]: data.strInput || data.boolInput
  });

  if (!data?.fieldErrors?.length) delete data.fieldErrors;

  if (stringValue) {
    data = produce(data, (draft) => {
      data.fieldErrors?.forEach(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (item, index) => (draft.fieldErrors![index] = item.replace(stringValue, '****'))
      );
    });
  }

  inputFill({ isAutoFilled: false, ...data } as InputFill, context);
};

export const tabClicked = (
  context: TrackingContextValue,
  data: TabClick & { tabIndex?: number },
  dimensions?: Record<string, Primitive | string[]>
) => {
  GAEvent({ action: 'Tab Pressed', label: data.tabName }, context, dimensions);
  tabClick(data, context);
};

export const linkClicked = (context: TrackingContextValue, data: LinkClick) => {
  GAEvent(
    { label: data.linkText ?? '', action: 'Hyperlink Pressed' },
    context,
    data.resolverTag ? { [GADimension.ResolverTag]: data.resolverTag } : {}
  );

  linkClick(data, context);
};

export const pageView = (context: TrackingContextValue, pathname: string) => {
  ReactGA.pageview(pathname);
  ReactGA.set({ [GADimension.PageOriginPath]: pathname });
  setCustomUrl(pathname);
  trackPageView();
};

export const resultsViewed = debounce(
  (context: TrackingContextValue, list: ResultsView, contents?: Entity[]) =>
    resultsView(
      list,
      produce(context, (draft) => {
        draft.entities = draft.entities || [];
        contents && draft.entities.push(...contents);
      })
    ),
  300
);

export const errorViewed = (context: TrackingContextValue, { errorCode }: Partial<ErrorView>) =>
  errorCode && errorView({ errorCode }, context);
